import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/brand/Wordmark";
import { ChatLog } from "@/components/trustee/ChatLog";
import { ChatInput } from "@/components/trustee/ChatInput";

const STAGE_FIRST = { business: "business_name", gmail: "gmail_consent", sources: "upload_consent", preferences: "retention" };

const Sidebar = ({ sidebar, planName, onSignOut, onEdit }) => (
  <aside className="w-[270px] shrink-0 hidden md:flex flex-col p-6 text-[var(--on-navy)]" style={{ background: "var(--navy)" }} data-testid="onboarding-sidebar">
    <span style={{ filter: "invert(1) hue-rotate(180deg)" }}><Wordmark showStatus={false} /></span>
    <div className="flex items-center gap-3 mt-9 pb-6 border-b border-white/10">
      <span className="w-5 h-5 rounded-full shrink-0" style={{ background: "var(--blue)" }} aria-hidden="true" />
      <div><div className="font-display font-bold text-lg leading-none">Trustee</div><div className="text-xs mt-1 text-[#8CA0B8]">Setting up your {planName} workspace</div></div>
    </div>
    <ol className="mt-8 flex flex-col gap-1" aria-label="Setup progress">
      {sidebar.map((s, i) => (
        <li key={s.id} data-testid={`ob-step-${s.id}`} data-state={s.state} aria-current={s.state === "active" ? "step" : undefined}
          onClick={s.state === "done" && onEdit ? () => onEdit(s.id) : undefined}
          className={`flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-[13.5px] font-bold transition-colors ${s.state === "active" ? "text-white bg-white/5 border-l-2" : s.state === "done" ? "text-[#7DA2F5] cursor-pointer hover:text-white" : "text-[#6E819A]"}`}
          style={s.state === "active" ? { borderColor: "var(--blue)" } : undefined}>
          <span className="w-6 h-6 rounded-full border-[1.5px] border-current flex items-center justify-center text-[11px] shrink-0" style={s.state === "done" ? { background: "#7DA2F5", color: "var(--ink)" } : undefined}>{s.state === "done" ? "✓" : i + 1}</span>
          {s.label}
        </li>
      ))}
    </ol>
    <div className="mt-auto text-[13px] text-[#6E819A] leading-relaxed">You approve everything before it counts.<br /><button className="font-bold text-[#7DA2F5]" onClick={onSignOut} data-testid="ob-signout">Sign out</button></div>
  </aside>
);

export default function Onboarding() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const liveRef = useRef(null);

  const load = () => api.get("/onboarding/state").then((r) => setSt(r.data));
  useEffect(() => { load().catch((e) => setErr(errorText(e))); }, []);
  useEffect(() => {
    const g = params.get("google");
    if (g && liveRef.current) liveRef.current.textContent = g === "connected" ? "Google mailbox connected." : "Google connection did not complete.";
  }, [params]);
  useEffect(() => { if (st?.completed) refresh(); }, [st?.completed]); // eslint-disable-line

  const startCore = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { setErr(errorText(e)); setBusy(false); }
  };

  const send = async (payload) => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/onboarding/answer", payload); setSt(data); }
    catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  };
  const goBack = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/onboarding/back"); setSt(data); }
    catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  };
  const editStep = (sid) => { setBusy(true); setErr(""); api.post("/onboarding/edit", { step_id: sid }).then((r) => setSt(r.data)).catch((e) => setErr(errorText(e))).finally(() => setBusy(false)); };
  useEffect(() => {
    if (!st || st.completed) return undefined;
    window.history.pushState({ ob: 1 }, "");
    const onPop = () => { window.history.pushState({ ob: 1 }, ""); goBack(); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [st?.step?.id, st?.completed]); // eslint-disable-line

  if (!st) return <div className="min-h-screen" style={{ background: "var(--paper)" }}>{err && <p className="p-6 ts-error">{err}</p>}</div>;

  return (
    <div className="min-h-screen flex" style={{ background: "var(--paper)" }} data-testid="onboarding-page">
      <Sidebar sidebar={st.sidebar} planName={st.plan_name} onSignOut={logout} onEdit={(stage) => STAGE_FIRST[stage] && editStep(STAGE_FIRST[stage])} />
      <main className="flex-1 min-w-0 flex flex-col px-5 sm:px-[5vw] py-8">
        <div className="md:hidden mb-4 flex items-center justify-between"><Wordmark size="text-lg" /><span className="eyebrow" data-testid="ob-mobile-step">{st.sidebar.find((s) => s.state === "active")?.label}</span></div>
        <div className="w-full max-w-[860px] flex-1 flex flex-col">
          <ChatLog messages={st.messages} step={st.step} />
          {st.step.id === "check_answers" && (() => {
            const rows = [
              ["business_name", "Business", st.data.business_name, "business_name"],
              ["business_size", "Team size", st.data.business_size, "business_size"],
              ["industry", "Industry", st.data.industry, "industry"],
              ["sub_count", "Subscriptions", st.data.sub_count, "sub_count"],
              ["country", "Country", st.data.country, "country"],
              ["province", "Province", st.data.province, "province"],
              ["gmail", "Google mailbox", st.google_connected ? (st.google_email || "Connected") : "Not connected", "gmail_consent"],
              ["uploads", "Uploads", st.documents?.length ? `${st.documents.length} file${st.documents.length !== 1 ? "s" : ""}` : "None", "upload_consent"],
              ["retention", "Keep documents", st.data.retention_days ? `${st.data.retention_days} days` : "30 days", "retention"],
            ].filter(([, , v]) => v != null && v !== "skip" && v !== "");
            return <div className="ts-card p-4 my-3" data-testid="check-answers"><p className="eyebrow mb-2">Your answers</p>{rows.map(([k, lbl, v, editId]) => <div key={k} className="flex justify-between py-1.5 text-sm border-b" style={{ borderColor: "var(--line-soft)" }}><span style={{ color: "var(--muted-text)" }}>{lbl}</span><span className="font-medium">{String(v)} <button className="ts-link text-xs ml-2" disabled={busy} onClick={() => editStep(editId)} data-testid={`edit-${editId}`}>Edit</button></span></div>)}</div>;
          })()}
          {st.completed && (
            <div className="ts-card p-5 my-3" data-testid="workspace-ready">
              {st.requested_core && <button className="ts-btn ts-btn-primary mr-2 mb-2" disabled={busy} onClick={startCore} data-testid="ob-pay-core">{busy ? "Opening secure payment…" : "Pay and start Core Plan"}</button>}
              <button className="ts-btn ts-btn-primary" onClick={() => { refresh(); nav("/app", { replace: true }); }} data-testid="ob-go-dashboard">Go to my dashboard</button>
            </div>
          )}
          <div ref={liveRef} className="sr-only" aria-live="polite" />
          {err && <p role="alert" className="ts-error mb-3" data-testid="ob-error">{err}</p>}
          {!st.completed && <ChatInput step={st.step} state={st} busy={busy} onSend={send} onRefresh={load} />}
          {st.step.id !== "intro" && !st.completed && <button className="ts-link text-xs mt-2 self-start" onClick={goBack} disabled={busy} data-testid="ob-back">Back</button>}
          <p className="text-xs mt-3" style={{ color: "var(--faint)" }}>{st.message_cap - st.messages_used <= 10 ? <>Messages used: <span className="num">{st.messages_used}/{st.message_cap}</span> · </> : null}Signed in as {user?.email}</p>
        </div>
      </main>
    </div>
  );
}
